import React, { useState, useEffect } from 'react';
import { X, BookOpen, DollarSign, Calendar, FileText, Save } from 'lucide-react';

const DailyLedgerForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  editingEntry = null, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    ledgerName: '',
    amount: '',
    description: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingEntry) {
      setFormData({
        ledgerName: editingEntry.ledgerName || '',
        amount: editingEntry.amount || '',
        description: editingEntry.description || '',
        transactionDate: editingEntry.transactionDate ? 
          new Date(editingEntry.transactionDate).toISOString().split('T')[0] : 
          new Date().toISOString().split('T')[0]
      });
    } else {
      setFormData({
        ledgerName: '',
        amount: '',
        description: '',
        transactionDate: new Date().toISOString().split('T')[0]
      });
    }
    setErrors({});
  }, [editingEntry, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.ledgerName.trim()) {
      newErrors.ledgerName = 'Ledger name is required';
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.transactionDate) {
      newErrors.transactionDate = 'Transaction date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      transactionType: 'expense' // Fixed to expense as per model
    };
    
    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0, 0, 0, 0.6)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 1000,
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        maxWidth: '650px', 
        width: '90%', 
        maxHeight: '90vh', 
        overflowY: 'auto', 
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        animation: 'slideUp 0.3s ease-out',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '1.75rem 2rem 1.5rem 2rem', 
          borderBottom: '1px solid #f1f5f9',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px'
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              color: '#0f172a', 
              fontSize: '1.5rem', 
              fontWeight: '700',
              letterSpacing: '-0.025em'
            }}>
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </h2>
            <p style={{ 
              margin: '0.25rem 0 0 0', 
              color: '#64748b', 
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {editingEntry ? 'Update the ledger entry details' : 'Create a new daily ledger entry'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            disabled={isLoading}
            style={{ 
              background: 'rgba(255, 255, 255, 0.8)', 
              border: '1px solid #e2e8f0', 
              borderRadius: '12px', 
              padding: '0.75rem',
              cursor: 'pointer', 
              color: '#64748b',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 1)';
              e.target.style.color = '#475569';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.8)';
              e.target.style.color = '#64748b';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
          {/* First Row - Ledger Name and Amount */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontWeight: '600', 
                color: '#1e293b', 
                marginBottom: '0.75rem', 
                fontSize: '0.875rem' 
              }}>
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  background: '#f1f5f9', 
                  borderRadius: '6px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <BookOpen size={14} style={{ color: '#64748b' }} />
                </div>
                Ledger Name
              </label>
              <input
                type="text"
                name="ledgerName"
                value={formData.ledgerName}
                onChange={handleChange}
                placeholder="Enter ledger name"
                style={{ 
                  width: '100%', 
                  padding: '0.875rem 1rem', 
                  border: `2px solid ${errors.ledgerName ? '#ef4444' : '#e2e8f0'}`, 
                  borderRadius: '12px', 
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  background: errors.ledgerName ? '#fef2f2' : '#ffffff',
                  color: '#1e293b',
                  fontWeight: '500'
                }}
                onFocus={(e) => {
                e.target.style.borderColor = errors.ledgerName ? '#ef4444' : '#6366f1';
                  e.target.style.boxShadow = errors.ledgerName ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.ledgerName ? '#ef4444' : '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={isLoading}
              />
              {errors.ledgerName && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  marginTop: '0.5rem',
                  color: '#ef4444', 
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  <div style={{ width: '4px', height: '4px', background: '#ef4444', borderRadius: '50%' }}></div>
                  {errors.ledgerName}
                </div>
              )}
            </div>
            
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontWeight: '600', 
                color: '#1e293b', 
                marginBottom: '0.75rem', 
                fontSize: '0.875rem' 
              }}>
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  background: '#f0fdf4', 
                  borderRadius: '6px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <DollarSign size={14} style={{ color: '#16a34a' }} />
                </div>
                Amount
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                style={{ 
                  width: '100%', 
                  padding: '0.875rem 1rem', 
                  border: `2px solid ${errors.amount ? '#ef4444' : '#e2e8f0'}`, 
                  borderRadius: '12px', 
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  background: errors.amount ? '#fef2f2' : '#ffffff',
                  color: '#1e293b',
                  fontWeight: '500'
                }}
                onFocus={(e) => {
                e.target.style.borderColor = errors.amount ? '#ef4444' : '#6366f1';
                  e.target.style.boxShadow = errors.amount ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.amount ? '#ef4444' : '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={isLoading}
              />
              {errors.amount && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  marginTop: '0.5rem',
                  color: '#ef4444', 
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  <div style={{ width: '4px', height: '4px', background: '#ef4444', borderRadius: '50%' }}></div>
                  {errors.amount}
                </div>
              )}
            </div>
          </div>
          
          {/* Transaction Date */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              fontWeight: '600', 
              color: '#1e293b', 
              marginBottom: '0.75rem', 
              fontSize: '0.875rem' 
            }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                background: '#fef3c7', 
                borderRadius: '6px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Calendar size={14} style={{ color: '#d97706' }} />
              </div>
              Transaction Date
            </label>
            <input
              type="date"
              name="transactionDate"
              value={formData.transactionDate}
              onChange={handleChange}
              style={{ 
                width: '100%', 
                padding: '0.875rem 1rem', 
                border: `2px solid ${errors.transactionDate ? '#ef4444' : '#e2e8f0'}`, 
                borderRadius: '12px', 
                fontSize: '0.875rem',
                transition: 'all 0.2s ease',
                background: errors.transactionDate ? '#fef2f2' : '#ffffff',
                color: '#1e293b',
                fontWeight: '500'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = errors.transactionDate ? '#ef4444' : '#6366f1';
                e.target.style.boxShadow = errors.transactionDate ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.transactionDate ? '#ef4444' : '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
              disabled={isLoading}
            />
            {errors.transactionDate && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginTop: '0.5rem',
                color: '#ef4444', 
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                <div style={{ width: '4px', height: '4px', background: '#ef4444', borderRadius: '50%' }}></div>
                {errors.transactionDate}
              </div>
            )}
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '2.5rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              fontWeight: '600', 
              color: '#1e293b', 
              marginBottom: '0.75rem', 
              fontSize: '0.875rem' 
            }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                background: '#e0e7ff', 
                borderRadius: '6px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FileText size={14} style={{ color: '#6366f1' }} />
              </div>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter description"
              rows="4"
              style={{ 
                width: '100%', 
                padding: '0.875rem 1rem', 
                border: `2px solid ${errors.description ? '#ef4444' : '#e2e8f0'}`, 
                borderRadius: '12px', 
                fontSize: '0.875rem',
                transition: 'all 0.2s ease',
                background: errors.description ? '#fef2f2' : '#ffffff',
                color: '#1e293b',
                fontWeight: '500',
                resize: 'vertical',
                minHeight: '120px',
                fontFamily: 'inherit',
                lineHeight: '1.5'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = errors.description ? '#ef4444' : '#6366f1';
                e.target.style.boxShadow = errors.description ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.description ? '#ef4444' : '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
              disabled={isLoading}
            />
            {errors.description && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginTop: '0.5rem',
                color: '#ef4444', 
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                <div style={{ width: '4px', height: '4px', background: '#ef4444', borderRadius: '50%' }}></div>
                {errors.description}
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '1rem', 
            paddingTop: '1.5rem', 
            borderTop: '1px solid #f1f5f9',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            margin: '0 -2rem -2rem -2rem',
            padding: '1.5rem 2rem 2rem 2rem',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{ 
                padding: '0.875rem 1.75rem', 
                borderRadius: '12px', 
                fontWeight: '600', 
                cursor: 'pointer', 
                transition: 'all 0.2s ease',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid #e2e8f0',
                color: '#475569',
                fontSize: '0.875rem',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 1)';
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{ 
                padding: '0.875rem 1.75rem', 
                borderRadius: '12px', 
                fontWeight: '600', 
                cursor: 'pointer', 
                transition: 'all 0.2s ease',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                border: 'none',
                color: 'white',
                fontSize: '0.875rem',
                minWidth: '140px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid transparent', 
                    borderTop: '2px solid currentColor', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  Saving...
                </div>
              ) : (
                <>
                  <Save size={16} />
                  {editingEntry ? 'Update Entry' : 'Add Entry'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(20px) scale(0.95); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DailyLedgerForm;
